import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { PowerBIResponse } from './reportsdashboard.model';
import { environment } from '../../environments/environment';
import { StateManagementService } from '../shared/services/state-management.service';

@Injectable({
  providedIn: 'root',
})
export class ReportsDashboardService {
  private readonly runtimeApiUrl = (window as { __APP_CONFIG__?: { apiUrl?: string } }).__APP_CONFIG__?.apiUrl;
  private readonly baseApiUrl = this.runtimeApiUrl || environment.apiUrl;
  private readonly apiUrl = this.baseApiUrl;
  private connectionId = '';

  constructor(private http: HttpClient, private stateManagementService: StateManagementService) { }

  getConnections(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/powerbi-connections`);
  }

  setConnectionId(id: string): void {
    this.connectionId = id;
  }

  getAccessToken(): Observable<string> {
    const url = `${this.apiUrl}/powerbi/access-token?connectionId=${this.connectionId}`;
    return this.http.get<string>(url);
  }

  getEmbedToken(reportId: string, instPk: string, datasetId?: string | null): Observable<PowerBIResponse> {
    let params = new HttpParams()
      .set('connectionId', this.connectionId)
      .set('reportPk', reportId)
      .set('instPk', instPk);
    if (datasetId) {
      params = params.set('datasetId', datasetId);
    }
    return this.http.get<PowerBIResponse>(`${this.apiUrl}/powerbi/embed-token`, { params });
  }

  getDepartments(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/departments`);
  }

  getInstitutions(deptId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/departments/${deptId}/institutions`);
  }


  getValidatedUser(): any | null {
    const data = this.stateManagementService.getLocalStorage('userValue');
    return data ? JSON.parse(data) : null;
  }

  getJwtPayload(): any | null {
    const token = this.stateManagementService.getLocalStorage('jwtToken');

    if (!token) {
      return null;
    }

    try {
      const payload = token.split('.')[1];
      return payload
        ? JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
        : null;
    } catch {
      return null;
    }
  }

  getSuperAdminFromToken(): boolean {
    const payload = this.getJwtPayload();
    return !!(payload?.superAdmin || payload?.isAdmin);
  }

  hasAccessToAllInstitutionsFromProfile(): boolean {
    const user = this.getValidatedUser();
    return user?.allowAllAccess === true;
  }

  getDepartmentPksFromToken(): string[] {
    const departments = this.getJwtPayload()?.department;

    if (!Array.isArray(departments)) {
      return [];
    }

    return departments
      .map((item: any) => String(item?.pk ?? item?.id ?? item))
      .filter(value => value !== 'undefined');
  }

  getAllowedInstitutionPksFromToken(): string[] {
    const institutions = this.getJwtPayload()?.allowedInstitutionPks;

    if (!Array.isArray(institutions)) {
      return [];
    }

    return institutions.map(String);
  }

  getReports(token: string, institutionId: string, userId: string): Observable<any> {
    const url = `${this.apiUrl}/reports?userId=${encodeURIComponent(userId)}&institution=${encodeURIComponent(institutionId)}`;
    return this.http.get<any>(url);
  }
}
